// Sync From Cloud Tool - Fetches app files from backend API and writes to Lightning FS
// Extends WorkspaceTool - has access to appType and toolSystem
// This is the primary tool for initializing/syncing a workspace with remote storage

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";
import { notifyWorkspaceServiceWorker } from "../../../../../../workspace/serviceWorkerBridge.js";
import JSZip from "jszip";

export class SyncFromCloudTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = "syncFromCloudTool";
  }

  createCancelledError(message = "Cloud pull cancelled") {
    const error = new Error(message);
    error.type = "cancelled";
    error.userMessage = message;
    return error;
  }

  throwIfCancelled(cancelSignal, message = "Cloud pull cancelled") {
    if (cancelSignal?.aborted) {
      throw this.createCancelledError(message);
    }
  }

  /**
   * Sync files from cloud storage to local filesystem
   * @param {string} appId - App ID to fetch files for
   * @param {boolean} [clearExisting=true] - Clear existing files before syncing
   * @returns {Promise<Object>} Result with operation status and file count
   */
  async syncFromCloud(appId, clearExisting = true) {
    try {
      // Validate appId
      if (!appId || typeof appId !== "string") {
        return this.returnError({}, "Valid app ID required");
      }

      // Route based on app type
      if (this.appType === "browser") {
        return await this.syncFromCloudForBrowser(appId, clearExisting);
      } else {
        return this.returnError(
          {},
          `App type '${this.appType}' not yet implemented for syncFromCloud`
        );
      }
    } catch (error) {
      logger.error(
        {
          tags: "tools|syncfromcloud|error",
          color1: "red",
        },
        `Failed to sync files from cloud for app: ${appId}`,
        error
      );

      console.error(
        `[SYNC] Failed to sync files from cloud for app: ${appId}`,
        error.message
      );

      // Return structured error information
      return this.returnError(
        {
          errorType: error.type || "unknown",
          status: error.status,
          endpoint: error.endpoint,
          userMessage: error.userMessage,
        },
        error.message
      );
    }
  }

  /**
   * Sync files from cloud for browser apps using Lightning FS
   */
  async syncFromCloudForBrowser(appId, clearExisting) {
    return this.syncFromCloudToPath(appId, clearExisting, "/files", true);
  }

  /**
   * Sync files from cloud into a temporary directory (no service worker notify)
   * @param {Object} [options] - Optional { onProgress: (percent, message) => void }
   */
  async syncFromCloudToTemp(appId, clearExisting = true, options = {}) {
    return this.syncFromCloudToPath(
      appId,
      clearExisting,
      "/__cloud/files",
      false,
      options
    );
  }

  async fetchFilesGitMetadata(appId) {
    try {
      const response = await fetch(`/api/apps/${appId}/files/git-metadata`, {
        credentials: "include",
      });

      if (response.status === 404) {
        try {
          const errorData = await response.json();
          if (errorData?.errorCode === "GIT_METADATA_NOT_FOUND") {
            return {
              success: true,
              exists: false,
              metadata: null,
            };
          }
        } catch (parseError) {
          // Fall through and treat malformed 404 responses as actual errors.
        }

        return this.returnError(
          {
            errorType: "git_metadata_not_found_unexpected",
            status: 404,
            endpoint: `/api/apps/${appId}/files/git-metadata`,
          },
          "Git metadata endpoint returned 404"
        );
      }

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;

        try {
          const text = await response.text();
          if (text) {
            errorMessage = text;
          }
        } catch (textError) {
          // Ignore response parsing failures for network errors.
        }

        const networkError = new Error(errorMessage);
        networkError.status = response.status;
        networkError.endpoint = `/api/apps/${appId}/files/git-metadata`;
        throw networkError;
      }

      const data = await response.json();
      return {
        success: true,
        exists: true,
        metadata: data?.metadata || null,
      };
    } catch (error) {
      logger.error(
        {
          tags: "tools|syncfromcloud|git-metadata",
          color1: "red",
        },
        `Failed to fetch git metadata for app ${appId}`,
        error
      );
      return this.returnError(
        {
          errorType: "git_metadata_fetch_failed",
          status: error?.status || null,
          endpoint: error?.endpoint || null,
        },
        error.message || "Failed to fetch git metadata"
      );
    }
  }

  /**
   * Sync all bundle types (files, chats, assets) from cloud into temporary directories
   * @param {Object} [options] - Optional { onProgress: (percent, message) => void }
   */
  async syncAllBundlesFromCloudToTemp(
    appId,
    membershipId = null,
    clearExisting = true,
    options = {}
  ) {
    const onProgress = options.onProgress;
    const cancelSignal = options.cancelSignal;
    const bundleTypes = ["files", "chats", "assets"];
    const requiredBundles = ["files"]; // Only "files" is required
    const results = {};
    const errors = [];

    for (let i = 0; i < bundleTypes.length; i++) {
      const bundleType = bundleTypes[i];
      const bundleStart = (i / bundleTypes.length) * 100;
      const bundleEnd = ((i + 1) / bundleTypes.length) * 100;
      const bundleOptions = {
        ...(onProgress
          ? {
              onProgress: (p, msg) =>
                onProgress(bundleStart + (p / 100) * (bundleEnd - bundleStart), msg),
            }
          : {}),
        cancelSignal,
      };
      try {
        this.throwIfCancelled(cancelSignal);
        const result = await this.syncBundleFromCloudToTemp(
          appId,
          membershipId,
          bundleType,
          clearExisting,
          bundleOptions
        );
        results[bundleType] = result;
      } catch (error) {
        if (error?.type === "cancelled" || error?.name === "AbortError") {
          return this.returnError(
            {
              errorType: "cancelled",
              cancelled: true,
            },
            "Cloud pull cancelled"
          );
        }
        // Only treat missing bundles as errors if they're required
        if (requiredBundles.includes(bundleType)) {
          errors.push({ type: bundleType, error: error.message });
          results[bundleType] = { success: false, error: error.message };
        } else {
          // Optional bundles (assets, chats) - treat missing as empty
          logger.log(
            {
              tags: "tools|syncfromcloud|optional",
              color1: "cyan",
            },
            `Optional bundle ${bundleType} not available - treating as empty`
          );
          results[bundleType] = { success: true, fileCount: 0, files: [] };
        }
      }
    }

    return {
      success: errors.length === 0,
      results,
      errors: errors.length > 0 ? errors : undefined,
    };
  }

  /**
   * Sync a specific bundle type from cloud into temporary directory
   * @param {Object} [options] - Optional { onProgress: (percent, message) => void }
   */
  async syncBundleFromCloudToTemp(
    appId,
    membershipId = null,
    bundleType = "files",
    clearExisting = true,
    options = {}
  ) {
    const cloudPath = `/__cloud/${bundleType}`;
    return this.syncBundleFromCloudToPath(
      appId,
      membershipId,
      bundleType,
      clearExisting,
      cloudPath,
      false,
      options
    );
  }

  /**
   * Sync a specific bundle type from cloud into a specific directory
   * @param {Object} [options] - Optional { onProgress: (percent, message) => void }
   */
  async syncBundleFromCloudToPath(
    appId,
    membershipId,
    bundleType,
    clearExisting,
    rootDir,
    notifyServiceWorker,
    options = {}
  ) {
    const onProgress = options.onProgress;
    const cancelSignal = options.cancelSignal;
    const report = (p, msg) => {
      if (typeof onProgress === "function") onProgress(p, msg);
    };

    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    logger.log(
      {
        tags: "tools|syncfromcloud|info",
        color1: "lime",
      },
      `Starting ${bundleType} bundle sync from cloud for app: ${appId}`
    );

    this.throwIfCancelled(cancelSignal);

    // Clear existing filesystem if requested
    if (clearExisting) {
      await this.clearFilesystemAt(fs, rootDir);
    }

    this.throwIfCancelled(cancelSignal);

    // Fetch bundle from backend API (reports 0-50% for fetch+extract)
    const files = await this.fetchBundleFromAPI(
      appId,
      membershipId,
      bundleType,
      {
        onProgress: (p, msg) => report(p * 0.5, msg),
        cancelSignal,
      }
    );

    this.throwIfCancelled(cancelSignal);

    if (!files || files.length === 0) {
      logger.log(
        {
          tags: "tools|syncfromcloud|info",
          color1: "lime",
        },
        `No ${bundleType} files found for app: ${appId}`
      );
      return this.returnSuccess(
        {
          appId,
          bundleType,
          fileCount: 0,
          files: [],
        },
        `No ${bundleType} files to sync`
      );
    }

    // Write all files to Lightning FS (reports 50-100%)
    const writtenFiles = [];
    const errors = [];
    const total = files.length;

    for (let i = 0; i < files.length; i++) {
      this.throwIfCancelled(cancelSignal);
      const file = files[i];
      if (!file?.path || file.content === undefined) {
        console.warn(
          `[SYNC] Skipping ${bundleType} file with missing path or content:`,
          file
        );
        errors.push({
          path: file?.path || "unknown",
          error: "Missing path or content",
        });
        continue;
      }

      try {
        await this.writeFileToFilesystemAt(
          file.path,
          file.content,
          fs,
          rootDir
        );
        writtenFiles.push(file.path);
        const written = writtenFiles.length;
        report(50 + (written / total) * 50, `Writing ${bundleType} (${written} of ${total})`);
      } catch (error) {
        if (error?.type === "cancelled" || error?.name === "AbortError") {
          throw error;
        }
        console.error(
          `[SYNC] Failed to write ${bundleType} file: ${file.path}`,
          error.message
        );
        errors.push({ path: file.path, error: error.message });
        logger.warn(
          {
            tags: "tools|syncfromcloud|write",
            color1: "yellow",
          },
          `Failed to write ${bundleType} file: ${file.path}`,
          error
        );
      }
    }

    if (notifyServiceWorker) {
      notifyWorkspaceServiceWorker({
        type: "WORKSPACE_APP_UPDATED",
        appId,
      });
    }

    logger.log(
      {
        tags: "tools|syncfromcloud|info",
        color1: "lime",
      },
      `${bundleType} bundle sync completed for app: ${appId}`,
      {
        totalFiles: files.length,
        writtenFiles: writtenFiles.length,
        errors: errors.length,
      }
    );
    if (errors.length > 0) {
      console.warn(`[SYNC] ${bundleType} sync errors:`, errors);
    }

    return this.returnSuccess(
      {
        appId,
        bundleType,
        fileCount: writtenFiles.length,
        files: writtenFiles,
        errors: errors.length > 0 ? errors : undefined,
        rootDir,
      },
      `Synced ${writtenFiles.length} ${bundleType} files successfully`
    );
  }

  /**
   * Sync files from cloud into a specific root directory
   * @param {Object} [options] - Optional { onProgress: (percent, message) => void }
   */
  async syncFromCloudToPath(
    appId,
    clearExisting,
    rootDir,
    notifyServiceWorker,
    options = {}
  ) {
    const onProgress = options.onProgress;
    const report = (p, msg) => {
      if (typeof onProgress === "function") onProgress(p, msg);
    };

    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    logger.log(
      {
        tags: "tools|syncfromcloud|info",
        color1: "lime",
      },
      `Starting file sync from cloud for app: ${appId}`
    );

    // Clear existing filesystem if requested
    if (clearExisting) {
      await this.clearFilesystemAt(fs, rootDir);
    }

    // Fetch files from backend API (reports 0-50% for fetch+extract)
    const files = await this.fetchFilesFromAPI(appId, { onProgress: (p, msg) => report(p * 50, msg) });

    if (!files || files.length === 0) {
      logger.log(
        {
          tags: "tools|syncfromcloud|info",
          color1: "lime",
        },
        `No files found for app: ${appId}`
      );
      return this.returnSuccess(
        {
          appId,
          fileCount: 0,
          files: [],
        },
        "No files to sync"
      );
    }

    // Write all files to Lightning FS (reports 50-100%)
    const writtenFiles = [];
    const errors = [];
    const total = files.length;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file?.path || file.content === undefined) {
        console.warn(
          `[SYNC] Skipping file with missing path or content:`,
          file
        );
        errors.push({
          path: file?.path || "unknown",
          error: "Missing path or content",
        });
        continue;
      }

      try {
        await this.writeFileToFilesystemAt(
          file.path,
          file.content,
          fs,
          rootDir
        );
        writtenFiles.push(file.path);
        const written = writtenFiles.length;
        report(50 + (written / total) * 50, `Writing files (${written} of ${total})`);
      } catch (error) {
        console.error(
          `[SYNC] Failed to write file: ${file.path}`,
          error.message
        );
        errors.push({ path: file.path, error: error.message });
        logger.warn(
          {
            tags: "tools|syncfromcloud|write",
            color1: "yellow",
          },
          `Failed to write file: ${file.path}`,
          error
        );
      }
    }

    if (notifyServiceWorker) {
      notifyWorkspaceServiceWorker({
        type: "WORKSPACE_APP_UPDATED",
        appId,
      });
    }

    logger.log(
      {
        tags: "tools|syncfromcloud|info",
        color1: "lime",
      },
      `File sync completed for app: ${appId}`,
      {
        totalFiles: files.length,
        writtenFiles: writtenFiles.length,
        errors: errors.length,
      }
    );
    if (errors.length > 0) {
      console.warn("[SYNC] Sync errors:", errors);
    }

    return this.returnSuccess(
      {
        appId,
        fileCount: writtenFiles.length,
        files: writtenFiles,
        errors: errors.length > 0 ? errors : undefined,
        rootDir,
      },
      `Synced ${writtenFiles.length} files successfully`
    );
  }

  /**
   * Fetch files from the backend API
   * @param {Object} [options] - Optional { onProgress: (percent, message) => void }
   */
  async fetchFilesFromAPI(appId, options = {}) {
    return this.fetchBundleFromAPI(appId, null, "files", options);
  }

  /**
   * Convert Uint8Array to base64 (for binary file content)
   */
  _uint8ArrayToBase64(buffer) {
    if (typeof btoa === "function") {
      const chunkSize = 8192;
      let binary = "";
      for (let i = 0; i < buffer.length; i += chunkSize) {
        const chunk = buffer.subarray(i, Math.min(i + chunkSize, buffer.length));
        binary += String.fromCharCode.apply(null, chunk);
      }
      return btoa(binary);
    }
    if (typeof Buffer !== "undefined") {
      return Buffer.from(buffer).toString("base64");
    }
    return "";
  }

  /**
   * Fetch a bundle as a streaming zip from the backend and unzip client-side.
   * Uses streaming endpoints (no server buffering) for scalability.
   * Returns same format as legacy JSON API: [{ path, content (base64), ... }]
   * @param {Object} [options] - Optional { onProgress: (percent, message) => void } - percent 0-1
   */
  async fetchBundleFromAPI(appId, membershipId = null, bundleType = "files", options = {}) {
    const onProgress = options.onProgress;
    const cancelSignal = options.cancelSignal;
    const report = (p, msg) => {
      if (typeof onProgress === "function") onProgress(p, msg);
    };

    try {
      this.throwIfCancelled(cancelSignal);
      report(0, "Downloading bundle...");
      let url;
      if (bundleType === "chats") {
        if (!membershipId) {
          logger.warn(
            { tags: "tools|syncfromcloud|chats", color1: "yellow" },
            "Skipping chat sync: missing membershipId"
          );
          return [];
        }
        url = `/api/app-memberships/${membershipId}/chats/bundle`;
      } else {
        url = `/api/apps/${appId}/files/bundle?type=${encodeURIComponent(bundleType)}`;
      }

      const response = await fetch(url, {
        credentials: "include",
        signal: cancelSignal,
      });

      if (!response.ok) {
        // For optional bundle types (assets, chats), 404 means "no bundle exists" - return empty array
        if (response.status === 404 && (bundleType === "assets" || bundleType === "chats")) {
          logger.log(
            {
              tags: "tools|syncfromcloud|optional",
              color1: "cyan",
            },
            `No ${bundleType} bundle found - treating as empty`
          );
          return [];
        }

        let errorMessage = `HTTP ${response.status}`;

        try {
          const text = await response.text();
          if (text) {
            errorMessage = text;
          }
        } catch (textError) {
          // Ignore text parsing errors
        }

        const networkError = new Error(errorMessage);
        networkError.status = response.status;
        networkError.endpoint = url;

        if (response.status === 404) {
          networkError.type = "not_found";
          networkError.userMessage = `${bundleType} bundle not found on server`;
        } else if (response.status === 403) {
          networkError.type = "forbidden";
          networkError.userMessage = `Access denied to ${bundleType} bundle`;
        } else if (response.status === 500) {
          networkError.type = "server_error";
          networkError.userMessage = `Server error while fetching ${bundleType} bundle`;
        } else if (response.status >= 400 && response.status < 500) {
          networkError.type = "client_error";
          networkError.userMessage = `Request error while fetching ${bundleType} bundle`;
        } else {
          networkError.type = "network_error";
          networkError.userMessage = `Network error while fetching ${bundleType} bundle`;
        }

        console.error(
          `[SYNC] ${bundleType} API call failed: ${errorMessage} (${response.status})`
        );
        throw networkError;
      }

      this.throwIfCancelled(cancelSignal);
      const blob = await response.blob();
      this.throwIfCancelled(cancelSignal);
      report(0.25, "Extracting files...");
      const zip = await JSZip.loadAsync(blob);
      const files = [];
      const entries = Object.values(zip.files).filter((e) => !e.dir);
      const totalEntries = entries.length;

      for (let idx = 0; idx < entries.length; idx++) {
        this.throwIfCancelled(cancelSignal);
        const entry = entries[idx];
        const normalizedPath = entry.name.replace(/\\/g, "/").replace(/^\/+/, "");
        if (!normalizedPath) continue;

        if (totalEntries > 0) {
          report(0.25 + (idx / totalEntries) * 0.75, `Extracting files (${idx + 1} of ${totalEntries})`);
        }

        let content;
        const ext = normalizedPath.split(".").pop()?.toLowerCase() || "";
        const textExtensions = ["html", "css", "js", "json", "txt", "md", "xml", "svg", "htm"];
        const isTextFile = textExtensions.includes(ext);

        if (isTextFile) {
          const str = await entry.async("string");
          this.throwIfCancelled(cancelSignal);
          content = typeof btoa !== "undefined"
            ? btoa(unescape(encodeURIComponent(str)))
            : Buffer.from(str, "utf8").toString("base64");
        } else {
          const uint8 = await entry.async("uint8array");
          this.throwIfCancelled(cancelSignal);
          content = this._uint8ArrayToBase64(uint8);
        }

        files.push({
          path: normalizedPath,
          size: 0,
          contentType: "application/octet-stream",
          encoding: "base64",
          content,
          lastModified: null,
        });
      }

      return files;
    } catch (error) {
      if (error?.type === "cancelled" || error?.name === "AbortError") {
        throw this.createCancelledError();
      }
      if (error.endpoint) {
        console.error(`[SYNC] ${bundleType} network error:`, error.message);
        throw error;
      }

      console.error(`[SYNC] ${bundleType} fetch error:`, error.message);
      const networkError = new Error(`Network error: ${error.message}`);
      networkError.type = "network_error";
      networkError.userMessage = "Unable to connect to server";
      networkError.originalError = error;
      throw networkError;
    }
  }

  /**
   * Clear the /files/ directory before sync
   */
  async clearFilesystem(fs) {
    return this.clearFilesystemAt(fs, "/files");
  }

  async clearFilesystemAt(fs, rootDir) {
    try {
      const targetDir = rootDir || "/files";
      // Check if target directory exists
      try {
        await fs.stat(targetDir);
        // Directory exists, clear it
        await this.removeDirectoryRecursive(targetDir, fs);
        // Recreate empty directory (ensure parents exist)
        await this.ensureDirectoryPath(targetDir, fs);
      } catch (error) {
        if (error.code === "ENOENT") {
          // Directory doesn't exist yet, create it
          await this.ensureDirectoryPath(targetDir, fs);
        } else {
          throw error;
        }
      }

      logger.log(
        {
          tags: "tools|syncfromcloud|clear",
          color1: "lime",
        },
        `Filesystem ${targetDir} directory cleared before sync`
      );
    } catch (error) {
      logger.warn(
        {
          tags: "tools|syncfromcloud|clear",
          color1: "yellow",
        },
        "Failed to clear filesystem",
        error
      );
    }
  }

  async ensureDirectoryPath(directoryPath, fs) {
    if (!directoryPath || directoryPath === "/") {
      return;
    }
    const segments = directoryPath.split("/").filter(Boolean);
    let currentPath = "";
    for (const segment of segments) {
      currentPath += `/${segment}`;
      try {
        await fs.stat(currentPath);
      } catch (error) {
        if (error.code === "ENOENT") {
          await fs.mkdir(currentPath);
        } else {
          throw error;
        }
      }
    }
  }

  /**
   * Recursively remove a directory
   */
  async removeDirectoryRecursive(dirPath, fs) {
    try {
      const entries = await fs.readdir(dirPath);

      for (const entry of entries) {
        const fullPath = dirPath === "/" ? `/${entry}` : `${dirPath}/${entry}`;

        const stats = await fs.stat(fullPath);
        const isDir =
          typeof stats.isDirectory === "function"
            ? stats.isDirectory()
            : stats.type === "dir";

        if (isDir) {
          await this.removeDirectoryRecursive(fullPath, fs);
        } else {
          await fs.unlink(fullPath);
        }
      }

      await fs.rmdir(dirPath);
    } catch (error) {
      logger.warn(
        {
          tags: "tools|syncfromcloud|rmdir",
          color1: "yellow",
        },
        `Failed to remove directory: ${dirPath}`,
        error
      );
    }
  }

  /**
   * Write a file to the filesystem with directory creation
   * Files are stored under /files/ directory
   */
  async writeFileToFilesystem(relativePath, base64Content, fs) {
    return this.writeFileToFilesystemAt(
      relativePath,
      base64Content,
      fs,
      "/files"
    );
  }

  async writeFileToFilesystemAt(relativePath, base64Content, fs, rootDir) {
    const normalizedPath = relativePath.startsWith("/")
      ? relativePath
      : `/${relativePath}`;

    const baseDir = rootDir || "/files";
    const filesystemPath = `${baseDir}${normalizedPath}`;

    const directoryPath =
      filesystemPath.split("/").slice(0, -1).join("/") || "/";
    await this.ensureDirectory(directoryPath, fs);

    const binary = this.decodeBase64(base64Content);
    await fs.writeFile(filesystemPath, binary);
  }

  /**
   * Ensure directory exists, creating parent directories as needed
   */
  async ensureDirectory(directoryPath, fs) {
    if (!directoryPath || directoryPath === "/") {
      return;
    }

    const segments = directoryPath.split("/").filter(Boolean);
    let currentPath = "";

    for (const segment of segments) {
      currentPath += `/${segment}`;
      try {
        await fs.stat(currentPath);
      } catch (error) {
        if (error.code === "ENOENT") {
          try {
            await fs.mkdir(currentPath);
          } catch (mkdirError) {
            // Directory might have been created in parallel; ignore
            if (mkdirError.code !== "EEXIST") {
              logger.warn(
                {
                  tags: "tools|syncfromcloud|mkdir",
                  color1: "yellow",
                },
                `Failed to create directory: ${currentPath}`,
                mkdirError
              );
            }
          }
        } else {
          throw error;
        }
      }
    }
  }

  /**
   * Decode base64 string to Uint8Array
   */
  decodeBase64(base64String) {
    if (typeof atob === "function") {
      const binaryString = atob(base64String);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    }
    if (typeof Buffer !== "undefined") {
      return Uint8Array.from(Buffer.from(base64String, "base64"));
    }
    throw new Error("Unable to decode base64 content");
  }
}
